import React, {useEffect, useState} from 'react';
import {Button, Form, Grid, Header, Icon, Input, Label, Modal, Pagination, Segment, Table} from 'semantic-ui-react';
import {useNavigate} from "react-router-dom";
import moment from "moment/moment";
import {toast} from "react-toastify";
import {SessionUser} from "../../models/session-user";
import {useBlockUI} from "../../shared/services/block-ui-service";
import SessionStorage from "../../store/session";
import {ToasterConfig} from "../../config/ToasterConfig";
import StringUtils from "../../shared/utils/string-utils";
import useClientService from "../../services/backoffice/hooks/useClientService";
import {ClientFilter} from "../../filters/client-filter";
import {ShortDto} from "../../models/short-dto";
import {CreateClientDriverRequest} from "../../models/requests/create-client-driver-request";
import {ClientDriverFilters} from "../../filters/client-driver-filters";
import {ClientDriver} from "../../models/clientDriver";
import useDriverService from "../../services/backoffice/hooks/useDriverService";
import TooltipInfo from "../tooltip/tooltip";

interface MotoristasListProps {
    clientId: string;
}

const ClientDriversListComponent: React.FC<MotoristasListProps> = ({clientId}) => {
    const [motoristas, setMotoristas] = useState<ClientDriver[]>([]);
    const [driverList, setdriverList] = useState<ShortDto[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [filter, setFilter] = useState<ClientFilter>();
    const [filterDriver, setFilterDriver] = useState<ClientDriverFilters>({clientId: clientId ?? null});
    const [totalPages, setTotalPages] = useState<number>();
    const [sessionUser, setSessionUser] = useState<SessionUser>();
    const {getClientDrivers, linkClientDriver, deleteClientDriver} = useClientService()
    const [modalClientDriver, setModalClientDriver] = useState<Partial<CreateClientDriverRequest>>({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const {getAllDriversShortDto} = useDriverService();
    const [showFilters, setShowFilters] = useState(false);
    const blockUI = useBlockUI();
    const navigate = useNavigate();
    useEffect(() => {
        blockUI?.setActive(loading)
    }, [loading]);
    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    useEffect(() => {
        async function updateSessionUser() {
            const user = await SessionStorage.getSession();
            setSessionUser(user);
        }

        updateSessionUser();
    }, []);

    useEffect(() => {
        async function listClientDrivers() {
            setLoading(true);
            const response = await getAllDriversShortDto().finally(() => setLoading(false));
            setdriverList(response);
        }

        listClientDrivers();
    }, [motoristas]);


    useEffect(() => {
        listMotoristas();
    }, [clientId]);

    async function listMotoristas() {
        setLoading(true);
        try {
            const response = await getClientDrivers(filterDriver);
            setMotoristas(response?.itens);
            setTotalPages(response.totalPages);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };


    async function deleteMotorista(motorista: any) {
        try {
            const confirm = window.confirm(`Tem certeza que deseja desfazer a ligação com o motorista ${motorista.driverName}?`);
            if (confirm) {
                await deleteClientDriver(clientId, motorista.driverId);
                toast.success('Ligação entre cliente e motorista desfeita com sucesso', ToasterConfig.success);
                setFilter(prevFilter => ({...prevFilter}));
                await listMotoristas();
            }
        } catch (error: any) {
            setLoading(false);
        }
    }


    const handleSubmit = async () => {
        try {
            setLoading(true);
            await linkClientDriver({...modalClientDriver, clientId: clientId})
                .then(() => {
                    toast.success('Motorista vinculado ao cliente com sucesso', ToasterConfig.success);
                    setIsModalOpen(false);
                    setModalClientDriver({});
                })
        } catch (error) {
        } finally {
            setLoading(false);
            await listMotoristas();
        }
    };


    return (
        <div>

            <Header as="h3" textAlign="center" color={'green'}>
                Motoristas do Cliente: {motoristas.length > 0 && (
                <span style={{color: 'black'}}>
                    {motoristas[0].clientName}
                </span>
            )}
            </Header>


            <Table.Row>

                <Table.HeaderCell>
                    <Button content="Voltar para lista de clientes"
                            icon='arrow left'
                            onClick={() =>
                                navigate('/backoffice/clients')} grey/>
                </Table.HeaderCell>

                {sessionUser?.userData?.profile === 'Admin' && (
                    <Table.HeaderCell>
                        <TooltipInfo
                            content="Clique para adicionar novos motoristas ao cliente"
                            trigger={
                                <Button
                                    color="green"
                                    onClick={() => setIsModalOpen(true)}
                                >
                                    <Icon name="plus" /> Adicionar Motoristas
                                </Button>
                            }
                        />
                    </Table.HeaderCell>
                )}

                <Table.HeaderCell colSpan="4">
                    <Button onClick={toggleFilters} color={"green"}>
                        <Icon name={showFilters ? 'hide' : 'unhide'}/>
                        {showFilters ? 'Ocultar filtros' : 'Exibir filtros'}
                    </Button>
                </Table.HeaderCell>

            </Table.Row>
            {showFilters && (
                <Segment>
                    <Grid stackable doubling padded>
                        <Grid.Row>
                            <Grid.Column width={8}>
                                <Input
                                    fluid
                                    id="driverName"
                                    icon="search"
                                    type="text"
                                    placeholder="Nome do motorista"
                                    value={filterDriver?.driverName ?? ''}
                                    onChange={(event) =>
                                        setFilterDriver({ ...filterDriver, driverName: event.target?.value })
                                    }
                                />
                            </Grid.Column>
                            <Grid.Column width={6}>
                                <Input
                                    fluid
                                    id="driverCpf"
                                    icon="search"
                                    type="number"
                                    placeholder="CPF do motorista"
                                    value={filterDriver?.driverCpf ?? ''}
                                    onChange={(event) =>
                                        setFilterDriver({ ...filterDriver, driverCpf: event.target?.value })
                                    }
                                />
                            </Grid.Column>
                            <Grid.Column width={2}>
                                    <Button primary onClick={listMotoristas}>Buscar</Button>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
            )}

            <Table color={"green"}>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>#</Table.HeaderCell>
                        <Table.HeaderCell>Nome</Table.HeaderCell>
                        <Table.HeaderCell>CPF</Table.HeaderCell>
                        <Table.HeaderCell>Operação</Table.HeaderCell>
                        <Table.HeaderCell>Criado em</Table.HeaderCell>
                        <Table.HeaderCell>Ativo</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {motoristas?.map((motorista) => (
                        <Table.Row key={motorista.driverId}>
                            <Table.Cell>
                                <Button.Group>
                                    <TooltipInfo content="Clique para desvincular este motorista" trigger={
                                        <Button
                                            color="red"
                                            onClick={() => deleteMotorista(motorista)}
                                            icon="broken chain"
                                            content="Desvincular"
                                        />
                                    } />
                                </Button.Group>
                            </Table.Cell>
                            <Table.Cell>{`${motorista.driverName ?? ''}`}</Table.Cell>
                            <Table.Cell>{StringUtils.formatCPF(motorista.driverCpf)}</Table.Cell>
                            <Table.Cell>{`${motorista.driverCpf ?? ''}`}</Table.Cell>
                            <Table.Cell>{moment(motorista.createdAt).format('DD/MM/YY HH:mm')}</Table.Cell>
                            <Table.Cell>
                                <Label key={motorista.driverId} color={!motorista.isActive ? 'red' : 'green'}>
                                    {motorista.isActive ? 'Sim' : 'Não'}
                                </Label>
                            </Table.Cell>
                        </Table.Row>

                    ))}
                </Table.Body>
            </Table>
            <Pagination
                totalPages={totalPages ?? 0}
                defaultActivePage={filterDriver?.Page ?? 1}
                onPageChange={(e, {activePage}) => {
                    setFilterDriver({...filterDriver, Page: activePage as number});
                }}
            />

            <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <Modal.Header>Selecione os motoristas a serem adicionados ao cliente</Modal.Header>
                <Modal.Content>
                    <Form size="large">
                        <Segment stacked>
                            <Form.Select
                                label="Cliente"
                                name="clientId"
                                placeholder="Selecione um cliente"
                                options={clientId ? [{key: clientId, text: clientId, value: clientId}] : []}
                                value={modalClientDriver?.clientId}
                                onChange={(evento: any, data: any) => {
                                    setModalClientDriver({
                                        ...modalClientDriver,
                                        clientId: data.value,
                                    });
                                }}
                                required
                                disabled
                            />

                            <Form.Select
                                label="Qual motorista a ser adicionado ao cliente?"
                                name="driverId"
                                placeholder="Selecione uma opção"
                                options={driverList}
                                value={modalClientDriver?.driversIds}
                                onChange={(evento: any, data: any) => {
                                    setModalClientDriver({
                                        ...modalClientDriver,
                                        driversIds: data.value,
                                    });
                                }}
                                multiple
                                search
                                clearable
                            />

                        </Segment>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button color="green" onClick={handleSubmit}>
                        Adicionar
                    </Button>
                    <Button color="red" onClick={() => setIsModalOpen(false)}>
                        Fechar
                    </Button>
                </Modal.Actions>
            </Modal>
        </div>
    );
};

export default ClientDriversListComponent

